import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { DateTime } from 'luxon'

import { v4 as uniqid } from 'uuid'

import PageWrapper from '@Layout/PageWrapper'
import Layout      from '@Layout/BackOffice'

import Radio from '@/Form/V2/Radio'

import ModelPicker from '@/Form/V2/ModelPicker'
import SubmitForm  from '@/Form/V2/SubmitForm'
import FormStatus  from '@/Form/FormStatus'

import LoadPrivateSale from '@fetcher/LoadPrivateSale'

import Loader from '@Pure/Loader'

import { num, capitalize, isTrue } from '@root/utility'

import FormsStore from '../../Stores/Forms'

const FORM_NAME = 'CancelPrivateSaleRegistration';

export default PageWrapper(function ExportPrivateSale (props)
{
	const [submit, setSubmit] = useState();
	const [submitting, setSubmitting] = useState(false);
	const [private_sale, setPrivateSale] = useState();
	const [loading, setLoading] = useState(true);
	const [status, setStatus] = useState({ });
	const [cancel_status, setCancelStatus] = useState({ });
	const [date_id, setDateId] = useState();

	const EC_id = FormsStore.useState(s => s[FORM_NAME]?.EC_id || '');

	const user_picker_filter = useMemo(function ()
	{
		return { EC_id: EC_id }
	},
	[EC_id]);

	const private_sale_id = private_sale?.id || '';

	useEffect(function ()
	{
		if (!private_sale_id) return;

		FormsStore.update(function (store)
		{
			store[FORM_NAME] = { };

			store[FORM_NAME].private_sale_id = private_sale_id;
		});
	},
	[private_sale_id]);

	const onSuccess = useCallback(function (private_sale)
	{
		setPrivateSale(private_sale);
		setLoading(false);
	},
	[]);

	const onError = useCallback(function (status)
	{
		setStatus(status.info ? { info: status.info } : status);
		setLoading(false);
	},
	[]);

	const setPickedDate = useCallback(function (e)
	{
		const value = e?.target?.value;

		if (!value) return;

		setDateId(value);

		setStatus({ });
	},
	[]);

	const radios = useMemo(function ()
	{
		if (!private_sale) return [];

		const NOW = DateTime.now();

		return private_sale.dates.map(function (infos)
		{
			const time = DateTime.fromISO(infos.date).set(
			{
				hour: num(infos.start.h, 0),
				minute: num(infos.start.m, 0)
			})
			.setLocale('fr');

			const start_date = time
			.toFormat('cccc d MMMM \'de\' H\'h\'mm');

			const end_date = time.set(
			{
				hour: num(infos.end.h, 0),
				minute: num(infos.end.m, 0)
			})
			.toFormat('H\'h\'mm');

			const is__past = NOW > time.plus({ hours: -num(private_sale.delta_h) });

			return (
				<Radio key={ infos.id }
					label={ capitalize(start_date + ' à ' + end_date) }
					name="date_id"
					value={ infos.id }
					checked={ date_id === infos.id }
					onChange={ setPickedDate }
					read-only={ is__past || isTrue(infos.__full) }
				/>
			);
		});
	},
	[date_id, private_sale, setPickedDate]);

	const sendForm = useCallback(function (e)
	{
		if (submitting) return;

		FormsStore.update(function (store)
		{
			store[FORM_NAME] ||= { };

			store[FORM_NAME]._submit_id = uniqid();
		});

		setSubmitting(true);
		setSubmit(uniqid());
	},
	[submitting]);

	const onCancelError = useCallback(function (update)
	{
		setSubmit(undefined);
		setCancelStatus(update);
		setSubmitting(false);
	},
	[]);

	const onCancelSuccess = useCallback(function (response)
	{
		setSubmit(undefined);
		setCancelStatus(response);
		setSubmitting(false);
	},
	[]);

	const is__disabled = !private_sale || !date_id;

	const cancel_loader = submitting ?
	(
		<Loader className="c-loader" bemit={ ['white', 'bg-red'] } />
	)
	: undefined;

	const cancel_section = private_sale ?
	(
		<div className="c-form c-form--private-sale-cancel">

			<fieldset className="o-form__fieldset u-full-width">

				<legend className="o-form__fieldset-legend">
					Annuler une inscription
				</legend>

				<div className="o-form__fieldset-content">

					<ModelPicker name="EC_id"
						label="Nom du CSE"
						model="enterprisecomitee"
						error={ cancel_status.fields?.['EC_id']?.error }
						bemit={ ['50%'] }
						form={ FORM_NAME }
						url="/autocomplete/ECs"
					/>

					<ModelPicker name="user_id"
						label="Membre de CSE (Nom/prénom)"
						model="user"
						error={ cancel_status.fields?.['user_id']?.error }
						bemit={ ['50%'] }
						form={ FORM_NAME }
						url="/autocomplete/users"
						custom={ user_picker_filter }
						key={ EC_id }
						read-only={ !EC_id }
					/>

				</div>

				<button type="button" className="c-btn c-btn--red u-margin-top-25"
					onClick={ sendForm }>
					Annuler l'inscription de ce membre
					{ cancel_loader }
				</button>

			</fieldset>

			{
				submit ?
				(
					<SubmitForm form={ FORM_NAME } id={ submit }
						key={ props.type }
						onError={ onCancelError }
						onSuccess={ onCancelSuccess }
						url="/private-sales/cancel-registration"
					/>
				)
				: undefined
			}
		</div>
	)
	: undefined;

	return (
		<Layout page_title="Export vente privée" history={ props.history }>

			<div className="page-content">

				<div className="u-full-width u-flex u-flex-wrap u-margin-bottom-30"
					style={ { gap: 30 } }>
					{ radios }
				</div>

				<a href={ is__disabled ? ''
					: ('/export/VP/' + private_sale?.slug + '/' + date_id) }
					className={ 'c-btn c-btn--extra c-btn--primary' + (is__disabled ? ' c-btn--disabled' : '') }
					target="_blank"
					rel="noreferrer"
					disabled={ is__disabled }>
					Télécharger
				</a>

				<FormStatus { ...status } />

				{ cancel_section }

				<FormStatus { ...cancel_status } />

			</div>

			<LoadPrivateSale slug={ getSlug() }
				onSuccess={ onSuccess }
				onError={ onError }
				admin
			/>

		</Layout>
	);
});

function getSlug ()
{
	let str = document.location.pathname.split('/exports')[0];

	return str.split('/gestion-des-ventes-privees/')[1];
}